import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, 
  ArrowUpRight, ArrowDownRight, ChevronRight, AlertTriangle, Award
} from 'lucide-react';

// Import mock data
import { storePerformance, productMix } from '../../data/analyticsData';

// Helper for formatting currency
const formatCurrency = (value) => {
  return value.toLocaleString('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        <div className="space-y-1.5">
          {payload.map((entry, index) => (
            <p 
              key={index} 
              className="text-sm" 
              style={{ color: entry.color }}
            >
              {entry.name}: {
                entry.name.includes('Sales') || entry.name.includes('Revenue') 
                  ? formatCurrency(entry.value) 
                  : entry.name.includes('Growth') || entry.name.includes('Margin')
                    ? `${entry.value.toFixed(1)}%`
                    : entry.value.toLocaleString()
              }
            </p>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Performance card component
const PerformanceCard = ({ item, rank, metric, isProduct = false, isTopPerformer = true }) => {
  const growth = isProduct ? item.growth : item.growth;
  const isPositive = growth >= 0;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
            isTopPerformer 
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
          }`}>
            <span className="text-sm font-semibold">{rank}</span>
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">{item.name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isProduct ? item.category : item.region}
            </p>
          </div>
        </div>
        <div className={`flex items-center text-sm ${
          isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {isPositive ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          <span>{Math.abs(growth).toFixed(1)}%</span>
        </div>
      </div>
      
      <div className="mt-3 flex justify-between items-center">
        <div className="text-lg font-semibold text-gray-900 dark:text-white">
          {isProduct 
            ? formatCurrency(item.sales)
            : formatCurrency(item.sales)
          }
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span>
            {isProduct 
              ? `${item.quantity.toLocaleString()} units`
              : `${item.transactions.toLocaleString()} txns`
            }
          </span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </div>
      </div>
    </div>
  );
};

const TopPerformers = ({ filters }) => {
  const [viewMode, setViewMode] = useState('stores'); // 'stores' or 'products'
  const [metric, setMetric] = useState('sales'); // 'sales', 'growth', 'transactions', 'margin'
  const [topStores, setTopStores] = useState([]);
  const [bottomStores, setBottomStores] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [bottomProducts, setBottomProducts] = useState([]);
  
  // Filter and sort data based on filters and selected metric
  useEffect(() => {
    // Filter stores based on region and type
    let filteredStores = [...storePerformance];
    
    if (filters.region !== 'all') {
      filteredStores = filteredStores.filter(store => 
        store.region.toLowerCase().includes(filters.region.toLowerCase())
      );
    }
    
    if (filters.storeType !== 'all') {
      filteredStores = filteredStores.filter(store => 
        store.type.toLowerCase().replace('+', '').includes(filters.storeType.toLowerCase())
      );
    }
    
    // Sort stores by selected metric
    const sortedStores = [...filteredStores].sort((a, b) => {
      if (metric === 'sales') return b.sales - a.sales;
      if (metric === 'growth') return b.growth - a.growth;
      if (metric === 'transactions') return b.transactions - a.transactions;
      return b.sales - a.sales; // Default to sales
    });
    
    setTopStores(sortedStores.slice(0, 5));
    setBottomStores([...sortedStores].reverse().slice(0, 5));
    
    // Process products data
    let filteredProducts = [...productMix];
    
    // Sort products by selected metric
    const sortedProducts = [...filteredProducts].sort((a, b) => {
      if (metric === 'sales') return b.sales - a.sales;
      if (metric === 'growth') return b.growth - a.growth;
      if (metric === 'margin') return b.margin - a.margin;
      if (metric === 'quantity') return b.quantity - a.quantity;
      return b.sales - a.sales; // Default to sales
    });
    
    setTopProducts(sortedProducts.slice(0, 5));
    setBottomProducts([...sortedProducts].reverse().slice(0, 5));
    
  }, [filters, metric]);
  
  // Colors for charts
  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];
  const NEGATIVE_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#06b6d4'];

  return (
    <div className="space-y-6">
      {/* Control Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {viewMode === 'stores' ? 'Store Performance Ranking' : 'Product Performance Ranking'}
            </h3>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Tabs */}
            <div className="flex items-center bg-gray-50 dark:bg-gray-700/30 rounded-lg p-1">
              <button
                onClick={() => setViewMode('stores')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  viewMode === 'stores'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Stores
              </button>
              <button
                onClick={() => setViewMode('products')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  viewMode === 'products'
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Products
              </button>
            </div>
            
            {/* Metric Selector */}
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm font-medium shadow-sm"
            >
              <option value="sales">Rank by Sales</option>
              <option value="growth">Rank by Growth</option>
              {viewMode === 'stores' ? (
                <option value="transactions">Rank by Transactions</option>
              ) : (
                <>
                  <option value="margin">Rank by Margin</option>
                  <option value="quantity">Rank by Quantity</option>
                </>
              )}
            </select>
          </div>
        </div>
      </div>
      
      {/* Main Content - Top & Bottom Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <Award className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Top Performers
            </h3>
          </div>
          
          <div className="space-y-4">
            {/* Performance Cards */}
            {viewMode === 'stores' ? (
              topStores.map((store, index) => (
                <PerformanceCard 
                  key={store.id}
                  item={store}
                  rank={index + 1}
                  metric={metric}
                  isProduct={false}
                  isTopPerformer={true}
                />
              ))
            ) : (
              topProducts.map((product, index) => (
                <PerformanceCard 
                  key={product.id}
                  item={product}
                  rank={index + 1}
                  metric={metric}
                  isProduct={true}
                  isTopPerformer={true}
                />
              ))
            )}
          </div>
          
          {/* Visualization */}
          <div className="mt-8 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={viewMode === 'stores' ? topStores : topProducts}
                margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey={metric} 
                  name={
                    metric === 'sales' ? 'Sales Revenue' :
                    metric === 'growth' ? 'Growth Rate' :
                    metric === 'transactions' ? 'Transaction Count' :
                    metric === 'margin' ? 'Profit Margin' :
                    'Units Sold'
                  }
                >
                  {(viewMode === 'stores' ? topStores : topProducts).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Bottom Performers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
              <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Needs Improvement
            </h3>
          </div>
          
          <div className="space-y-4">
            {/* Performance Cards */}
            {viewMode === 'stores' ? (
              bottomStores.map((store, index) => (
                <PerformanceCard 
                  key={store.id}
                  item={store}
                  rank={index + 1}
                  metric={metric}
                  isProduct={false}
                  isTopPerformer={false}
                />
              ))
            ) : (
              bottomProducts.map((product, index) => (
                <PerformanceCard 
                  key={product.id}
                  item={product}
                  rank={index + 1}
                  metric={metric}
                  isProduct={true}
                  isTopPerformer={false}
                />
              ))
            )}
          </div>
          
          {/* Visualization */}
          <div className="mt-8 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={viewMode === 'stores' ? bottomStores : bottomProducts}
                margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey={metric} 
                  name={
                    metric === 'sales' ? 'Sales Revenue' :
                    metric === 'growth' ? 'Growth Rate' :
                    metric === 'transactions' ? 'Transaction Count' :
                    metric === 'margin' ? 'Profit Margin' :
                    'Units Sold'
                  }
                >
                  {(viewMode === 'stores' ? bottomStores : bottomProducts).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={NEGATIVE_COLORS[index % NEGATIVE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Duplicate Product Names Section - Only shown for products view */}
      {viewMode === 'products' && (
        <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-4 border border-amber-100 dark:border-amber-800/20">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-800/30">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-300">Potential Duplicate Products</h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                The following product names appear more than 3 times in the system and may need consolidation:
              </p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Show mock duplicate product names */}
                {['Espresso', 'Latte', 'Turkish Coffee', 'Croissant'].map((name, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-amber-100/50 dark:bg-amber-800/20 rounded-md">
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-300">{name}</span>
                    <span className="text-xs bg-amber-200 dark:bg-amber-800/50 px-1.5 py-0.5 rounded-full text-amber-800 dark:text-amber-300">
                      {4 + Math.floor(Math.random() * 3)} variations
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Insights Section */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800/30">
            <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-300">Performance Insights</h3>
            <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              {viewMode === 'stores' ? (
                <>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Mall locations like Riyadh Gallery and Red Sea Mall consistently outperform street locations.</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Stores with the lowest performance are concentrated in smaller cities and require additional marketing support.</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Consider expanding high-performing store formats to regions with untapped potential.</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Signature coffee blends are the top revenue generators across all regions.</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Seasonal products show high variance in performance and should be carefully tracked.</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Consider phasing out or refreshing underperforming product lines to improve overall margins.</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span>•</span>
                    <span>Product naming inconsistencies should be addressed to improve inventory management and sales tracking.</span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopPerformers; 